import { clearSession, getAccessToken, isTokenExpired, setAccessToken } from '@/lib/session';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000/api';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface ApiFetchOptions extends RequestInit {
  skipAuth?: boolean;
  method?: HttpMethod;
}

export interface ApiErrorDetail {
  field: string;
  message: string;
}

/** Everything the API's error envelope can carry beyond the message. */
export interface ApiErrorBody {
  success?: false;
  message?: string;
  error?: string;
  /** Our own vocabulary, allowlisted server-side - see backend/src/middleware/errorHandler.js. */
  code?: string;
  requiredFeature?: string;
  limitKey?: string;
  limit?: number;
  existingContactId?: string;
  errors?: ApiErrorDetail[];
}

export class ApiError extends Error {
  status: number;
  code?: string;
  limitKey?: string;
  limit?: number;
  requiredFeature?: string;
  /** The parsed envelope, so a caller needing something not promoted above still has it. */
  body?: ApiErrorBody;

  constructor(message: string, status: number, body?: ApiErrorBody) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
    this.body = body;
    this.code = body?.code;
    this.limitKey = body?.limitKey;
    this.limit = body?.limit;
    this.requiredFeature = body?.requiredFeature;
  }
}

// ---------------------------------------------------------------------------------------------
// Session renewal
//
// The access token lasts 15 minutes; the session behind it lasts as long as the httpOnly refresh
// cookie does. Every request goes through authFetch, which renews the token when it has lapsed and
// retries once — so a user coming back to a tab left open overnight just carries on. When the
// session itself is over, the refresh clears it and the route guards take the user to /login.
// ---------------------------------------------------------------------------------------------

// The codes backend/src/middleware/auth.js puts on its 401s. Only these mean "refresh and retry":
// login and changePassword answer 401 for a wrong password, and refreshing on those would sign the
// user out for a typo.
const ACCESS_TOKEN_REJECTED = new Set(['token_expired', 'unauthenticated']);

const sessionEnded = () => new ApiError('Your session has expired. Please sign in again.', 401, {
  code: 'session_expired',
});

let refreshInFlight: Promise<string | null> | null = null;

// Serialises refreshes across tabs. Every tab shares the one refresh cookie, and each refresh
// rotates it; two tabs refreshing at once would have the second present a token the first has just
// retired. The server forgives that for a few seconds, but not having the race is better.
function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  return navigator.locks ? navigator.locks.request('targetna-auth-refresh', fn) : fn();
}

async function requestRefresh(): Promise<string | null> {
  const response = await fetch(`${API_BASE_URL}/auth/refresh`, { method: 'POST', credentials: 'include' });
  if (response.status === 401) {
    clearSession();
    return null;
  }
  if (!response.ok) {
    // Anything else (a 5xx, a 429) says nothing about the session, so it stays.
    const { message, body } = await parseErrorBody(response);
    throw new ApiError(message, response.status, body);
  }
  const { data } = await response.json();
  setAccessToken(data.token);
  return data.token as string;
}

/**
 * Trade the refresh cookie for a new access token.
 *
 * Resolves to the token, or to null when the session is over — in which case it has been cleared
 * and the guards are already redirecting to /login. Rejects only when the server could not be
 * reached or failed: a network blip must never sign anybody out.
 *
 * @param staleToken the token that was refused, so a refresh another tab already made is reused
 *   rather than repeated.
 */
export function refreshAccessToken(staleToken: string | null = getAccessToken()): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = withRefreshLock(async () => {
      const current = getAccessToken();
      if (current && current !== staleToken && !isTokenExpired(current)) return current;
      return requestRefresh();
    }).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function isAccessTokenRejection(response: Response) {
  if (response.status !== 401) return false;
  try {
    const body = (await response.clone().json()) as ApiErrorBody;
    return ACCESS_TOKEN_REJECTED.has(body?.code ?? '');
  } catch {
    return false;
  }
}

/**
 * `fetch` against the API with the session attached: the bearer token, the refresh cookie, and one
 * transparent renew-and-retry when the token has lapsed. Returns the raw Response, for the callers
 * that need a blob rather than the JSON envelope; everything else wants apiFetch.
 */
export async function authFetch(path: string, options: ApiFetchOptions = {}): Promise<Response> {
  const { skipAuth, ...init } = options;
  const send = (token: string | null) => fetch(`${API_BASE_URL}${path}`, {
    ...init,
    // The refresh cookie is scoped to /api/auth, so this only carries it to the auth endpoints that
    // read it. Needed in development, where the API is on another port and so another origin.
    credentials: 'include',
    headers: token
      ? { ...(init.headers as Record<string, string> || {}), Authorization: `Bearer ${token}` }
      : init.headers,
  });

  if (skipAuth) return send(null);

  let token = getAccessToken();
  // Known to be dead already: renew first rather than spend a round trip on a certain 401.
  if (token && isTokenExpired(token)) {
    token = await refreshAccessToken(token);
    if (!token) throw sessionEnded();
  }

  const response = await send(token);
  if (!(await isAccessTokenRejection(response))) return response;

  const renewed = await refreshAccessToken(token);
  if (!renewed) throw sessionEnded();
  return send(renewed);
}

/** Sign out: end the session server-side, then forget it here. */
export async function logout() {
  try {
    await fetch(`${API_BASE_URL}/auth/logout`, { method: 'POST', credentials: 'include' });
  } catch {
    // Unreachable server: still sign out locally. The session lapses on its own.
  }
  clearSession();
}

export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  // FormData writes its own multipart Content-Type, boundary and all. Ours would overwrite it with
  // a boundary-less value and the server would fail to parse a body that is perfectly well formed.
  if (options.body instanceof FormData) {
    delete headers['Content-Type'];
  }

  const response = await authFetch(path, { ...options, headers });

  if (!response.ok) {
    const { message, body } = await parseErrorBody(response);
    throw new ApiError(message, response.status, body);
  }

  if (response.status === 204) {
    return {} as T;
  }

  const payload = await response.json();
  const data = typeof payload === 'object' && payload !== null && 'data' in payload ? payload.data : payload;
  return data as T;
}

// Message AND envelope together, because a response body can only be read once - the previous
// message-only reader is what left `code`, `limitKey` and `limit` unreachable, so a 402 naming the
// exact plan limit arrived in the UI as a bare sentence with nothing to key copy off.
async function parseErrorBody(response: Response): Promise<{ message: string; body?: ApiErrorBody }> {
  try {
    const data = (await response.json()) as ApiErrorBody;
    const message = data?.error || data?.message || response.statusText || 'Unknown error';
    return { message, body: data ?? undefined };
  } catch {
    return { message: response.statusText || 'Unknown error' };
  }
}

export function getApiBaseUrl() {
  return API_BASE_URL;
}
